import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import {
  PDFFIleType,
  FileErrorIcon,
  TrashIcon,
  UploadCloudIcon,
  CheckCircleOutlineIcon,
  PNGFIleType,
  JPGFIleType,
  XLSFIleType,
} from "../../assets/svgs";
import { FILE_ACCEPT_MAP, FILE_UPLOAD_STATUS } from "../../constants";
import { useEffect, useRef, useState } from "react";
import useApp from "../../hooks/useApp";
import { getSvgComponent } from "../../utils/svgUtils";
import "./styles.scss";
import {
  getFileExtension,
  isAcceptedMime,
  MIME_TO_EXT,
  resolveFileFormat,
} from "../../utils/document";

const FileUpload = ({
  fileKey = "default",
  onClickTryAgain,
  fileName,
  isRequired = false,
  docType,
  accept = "image/*,application/pdf",
  maxSize = 10 * 1024 * 1024,
  showDelete = true,
  onFileChange,
  onFileDelete,
  index,
  fileType,
  rightIcon,
  toolTip,
  uploadedDocFormat,
  isUploading = false,
  isCompleted = false,
  isAlreadyUploaded = false,
  uploadedFileName = null,
  uploadedFileSize = null,
  cardStyle = {},
  fileUploadStatus = {},
  documentSize,
  ...props
}) => {
  const location = useLocation();
  const { pathname } = location;
  const routeName = pathname.split("/")[1];
  const { t } = useTranslation();
  const fileReaderRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const {
    isRTL,
    fileUploadDetails,
    fileUploadProgress,
    handleSetFileUploadProgress,
    handleSetFileUploadDetails,
    handleResetFile,
    handleSetFileUploadStatus,
  } = useApp();

  const cleanName = (name = "") => name.split("__g_")[0];
  const currentFileStatus =
    fileUploadStatus[fileKey] || FILE_UPLOAD_STATUS.NOT_STARTED;
  const currentFileDetails = fileUploadDetails[fileKey] || {};
  const currentFileProgress = fileUploadProgress[fileKey] || 0;

  const showAlreadyUploaded = isAlreadyUploaded && !currentFileDetails?.name;

  const formatFileSize = (
    bytes,
    decimals = 1,
    rounding = "round" // "ceil" | "floor"
  ) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    const factor = Math.pow(10, decimals);
    let value;
    if (rounding === "ceil") value = Math.ceil(mb * factor) / factor;
    else if (rounding === "floor") value = Math.floor(mb * factor) / factor;
    else value = Math.round(mb * factor) / factor;
    return `${value.toFixed(decimals)}`;
  };

  const handleFileUpload = (e) => {
    if (isAlreadyUploaded) return;

    setErrorMessage(null);

    // Prevent if already uploading
    if (currentFileStatus === FILE_UPLOAD_STATUS.IN_PROGRESS) {
      console.warn("File is already uploading");
      return;
    }

    // Cancel any existing FileReader
    if (fileReaderRef.current) {
      fileReaderRef.current.abort();
      fileReaderRef.current = null;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    const acceptedTypes = accept
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    const mime = (file.type || "").toLowerCase();
    const ext = getFileExtension(file.name);

    const acceptedExts = acceptedTypes
      .map((m) => MIME_TO_EXT[m])
      .filter(Boolean);

    const isTypeValid =
      (mime && isAcceptedMime(file, accept)) ||
      (ext && acceptedExts.includes(ext));

    if (!isTypeValid) {
      const errorMsg =
        t(`${routeName}.invalid_file_type`, {
          acceptedExts: acceptedExts
            .map((i) => i.toLocaleUpperCase())
            .join(isRTL ? " ،" : ", "),
        }) || "Invalid file type. Please upload a valid file.";
      setErrorMessage(errorMsg);
      console.error("Invalid file type", { mime, ext, accept });
      return;
    }

    if (file.size > maxSize) {
      const maxSizeFormatted = formatFileSize(maxSize, 1, "floor");
      const fileSizeFormatted = formatFileSize(file.size, 1, "ceil");
      const errorMsg =
        t(`${routeName}.file_too_large`, {
          maxSize: maxSizeFormatted,
          fileSize: fileSizeFormatted,
        }) ||
        `File is too large. Maximum size is ${maxSizeFormatted}. Your file is ${fileSizeFormatted}.`;

      setErrorMessage(errorMsg);
      console.error("File too large");
      return;
    }

    // Set initial state
    handleSetFileUploadStatus(fileKey, FILE_UPLOAD_STATUS.IN_PROGRESS);
    handleSetFileUploadProgress(fileKey, 1);
    handleSetFileUploadDetails(fileKey, {
      name: file.name,
      size: `${(file.size / 1024).toFixed(0)} ${t(`${routeName}.file_size_kb`)}`,
      file,
      type: docType,
    });

    // Create new FileReader instance
    const reader = new FileReader();
    fileReaderRef.current = reader;

    reader.onloadstart = () => {
      handleSetFileUploadProgress(fileKey, 5);
    };

    reader.onload = (event) => {
      const base64String = event.target.result;
      const updated = {
        name: file.name,
        size: `${(file.size / 1024).toFixed(0)} ${t(`${routeName}.file_size_kb`)}`,
        file,
        base64: base64String,
        type: docType,
      };

      handleSetFileUploadDetails(fileKey, updated);

      if (onClickTryAgain) {
        onClickTryAgain({ file, base64: base64String, type: docType }, fileKey);
      }

      if (onFileChange) {
        onFileChange({ file, base64: base64String, type: docType }, fileKey);
      }

      fileReaderRef.current = null;
    };

    reader.onerror = (error) => {
      console.error("File reading error:", error);
      const errorMsg =
        t(`${routeName}.file_upload_error`) ||
        "Error uploading file. Please try again.";
      setErrorMessage(errorMsg);
      handleSetFileUploadProgress(fileKey, 0);
      handleSetFileUploadStatus(fileKey, FILE_UPLOAD_STATUS.FAILED);
      handleSetFileUploadDetails(fileKey, {});
      fileReaderRef.current = null;
    };

    reader.onabort = () => {
      console.log("File reading aborted");
      handleSetFileUploadProgress(fileKey, 0);
      handleSetFileUploadStatus(fileKey, FILE_UPLOAD_STATUS.NOT_STARTED);
      handleSetFileUploadDetails(fileKey, {});
      fileReaderRef.current = null;
    };

    reader.readAsDataURL(file);
  };

  const handleTryAgain = () => {
    setErrorMessage(null);
    handleResetFile(fileKey);
    handleSetFileUploadStatus(fileKey, FILE_UPLOAD_STATUS.NOT_STARTED);
    handleSetFileUploadProgress(fileKey, 0);

    const input = document.getElementById(`file-input-${fileKey}`);
    if (input) {
      input.value = "";
      input.click();
    }

    if (onClickTryAgain) {
      onClickTryAgain(null, fileKey);
    }
  };

  const handleOnClickDelete = () => {
    setErrorMessage(null);

    if (fileReaderRef.current) {
      fileReaderRef.current.abort();
      fileReaderRef.current = null;
    }

    const input = document.getElementById(`file-input-${fileKey}`);
    if (input) input.value = "";

    handleResetFile(fileKey);

    if (onFileDelete) {
      onFileDelete(fileKey);
    }

    if (onFileChange) {
      onFileChange(null, fileKey);
    }
  };

  const handleFileTypeIcon = (resolvedType) => {
    switch (resolvedType) {
      case FILE_ACCEPT_MAP.PDF:
        return PDFFIleType;

      case FILE_ACCEPT_MAP.XLS:
      case FILE_ACCEPT_MAP.XLSX:
        return XLSFIleType;

      case FILE_ACCEPT_MAP.PNG:
        return PNGFIleType;

      case FILE_ACCEPT_MAP.JPG:
      case FILE_ACCEPT_MAP.JPEG:
        return JPGFIleType;

      default:
        return PDFFIleType;
    }
  };

  useEffect(() => {
    return () => {
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <div
        className={`file-upload-card ${
          currentFileDetails?.name || isAlreadyUploaded
            ? "uploaded"
            : "not-uploaded"
        } ${isUploading ? "uploading" : ""}`}
        style={{
          border:
            currentFileStatus === FILE_UPLOAD_STATUS.FAILED || errorMessage
              ? "2px solid var(--bs-danger-500)"
              : isAlreadyUploaded
                ? "2px solid var(--bs-success-500)"
                : "1px solid var(--bs-neutral-300)",
          opacity: isUploading ? 0.7 : 1,
          pointerEvents: isUploading ? "none" : "auto",
          marginBottom: errorMessage ? 0 : "20px",
          ...cardStyle,
        }}
        {...props}
      >
        <input
          type="file"
          id={`file-input-${fileKey}`}
          accept={accept}
          hidden
          onChange={handleFileUpload}
          disabled={isUploading || isAlreadyUploaded}
        />

        <div className="left-section">
          <div
            className={
              currentFileDetails?.name || showAlreadyUploaded
                ? ""
                : "file-icon-box"
            }
            onClick={() =>
              !isUploading &&
              !isAlreadyUploaded &&
              document.getElementById(`file-input-${fileKey}`).click()
            }
          >
            <img
              src={
                currentFileStatus === FILE_UPLOAD_STATUS.FAILED || errorMessage
                  ? FileErrorIcon
                  : showAlreadyUploaded
                    ? handleFileTypeIcon(resolveFileFormat(uploadedDocFormat))
                    : currentFileDetails?.file
                      ? handleFileTypeIcon(
                          resolveFileFormat(currentFileDetails.file)
                        )
                      : UploadCloudIcon
              }
              className="file-icon"
              alt=""
            />
          </div>

          <div
            className="file-info"
            style={{
              paddingRight: currentFileProgress === 100 && !isRTL ? "22px" : 0,
              paddingLeft: currentFileProgress === 100 && isRTL ? "22px" : 0,
            }}
          >
            <div className="file-title-container">
              <p className="file-title">
                {showAlreadyUploaded
                  ? cleanName(uploadedFileName) ||
                    t(`${routeName}.${fileName}`, { vatReportCount: index + 1 })
                  : currentFileDetails?.name ||
                    t(`${routeName}.${fileName}`, {
                      vatReportCount: index + 1,
                    })}
              </p>

              {rightIcon ? (
                <>
                  <Tooltip id="my-tooltip" className="file-title-tooltip" />
                  <img
                    src={getSvgComponent(rightIcon)}
                    alt="file title icon"
                    className="file-title-icon"
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content={t(`${routeName}.${toolTip}`, {
                      vatReportCount: index + 1,
                    })}
                  />
                </>
              ) : null}

              {currentFileProgress === 100 || isAlreadyUploaded ? (
                <div className="right-section">
                  <img
                    src={TrashIcon}
                    className="trash-icon"
                    onClick={handleOnClickDelete}
                    alt="delete"
                  />
                </div>
              ) : null}
            </div>

            {(currentFileDetails?.name || showAlreadyUploaded) &&
            currentFileStatus !== FILE_UPLOAD_STATUS.FAILED ? (
              <>
                <div className="file-meta">
                  <span className="file-size-value">
                    {showAlreadyUploaded
                      ? formatFileSize(uploadedFileSize) || "Unknown size"
                      : currentFileDetails?.size}
                  </span>
                  <span className="separator" />
                  <img
                    src={
                      isAlreadyUploaded || currentFileProgress >= 100
                        ? CheckCircleOutlineIcon
                        : UploadCloudIcon
                    }
                    className="status-icon"
                    alt="status"
                  />
                  <span
                    className={`status ${
                      isAlreadyUploaded || currentFileProgress >= 100
                        ? "completed"
                        : "uploading"
                    }`}
                  >
                    {isAlreadyUploaded
                      ? t("global.loading_completed")
                      : currentFileProgress < 100
                        ? t("global.uploading...")
                        : t("global.loading_completed")}
                  </span>
                </div>
                {!isAlreadyUploaded && (
                  <div className="upload-progress-container">
                    <div
                      className={`progress-bar ${
                        currentFileProgress < 100
                          ? "progress-bar-in-progress"
                          : "progress-bar-completed"
                      }`}
                    >
                      <div
                        className={
                          currentFileProgress < 100
                            ? "progress-fill-percent-in-progress"
                            : "progress-fill-percent-completed"
                        }
                        style={{ width: `${currentFileProgress}%` }}
                      />
                    </div>
                    <div
                      className={`progress-percent ${
                        isRTL ? "rtl-progress-percent " : ""
                      }`}
                    >
                      {currentFileProgress}%
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="upload-meta">
                <span
                  className="upload-text"
                  onClick={() =>
                    currentFileStatus === FILE_UPLOAD_STATUS.FAILED
                      ? handleTryAgain()
                      : !isUploading &&
                        !isAlreadyUploaded &&
                        document.getElementById(`file-input-${fileKey}`).click()
                  }
                >
                  {isAlreadyUploaded
                    ? t(`${routeName}.already_uploaded`)
                    : currentFileStatus === FILE_UPLOAD_STATUS.FAILED ||
                        errorMessage
                      ? t(`${routeName}.try_again`)
                      : t(`${routeName}.click_to_upload`)}
                </span>
                <span className="upload-size">
                  {t(`${routeName}.${fileType}`, {
                    documentSize: documentSize ? documentSize : 10,
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {errorMessage && <p className="error-text">{errorMessage}</p>}
    </>
  );
};

export default FileUpload;
